<template>
<div>
    <b-card :class="embedded ? 'm-0 shadow-none' : 'm-2'">
    <!-- Nombre ticket -->
    <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex">
        <h3 class="h4 text-capitalize mb-0 font-weight-bold mr-2">
            Ticket {{ ticket ? ticket.name : "" }}
        </h3>
        <b-badge v-if="ticket" pill :variant="badgeFor(ticket.estado)">
            Estado: {{ ticket.estado }}
        </b-badge>
        </div>

        <b-button v-if="!embedded" variant="outline-secondary" size="sm" @click="goBack">
        <feather-icon icon="ArrowLeftIcon" size="14" />
        <span class="align-middle ml-50 text-capitalize">Volver</span>
        </b-button>
    </div>
    <hr class="mt-2 mb-2" />

    <div v-if="loading" class="text-center my-3">
        <b-spinner label="Loading" variant="primary" />
    </div>

    <div v-else-if="!ticket" class="text-muted py-2">
        No se encontró el ticket #{{ id }}.
    </div>

    <div v-else class="tk-grid" :class="{ 'tk-grid--embedded': embedded }">
        <!-- Columna 1: Datos / Análisis / Resolución / Historial / Editar -->
        <ticket-info-column
        :key="'info-' + id"
        :ticket="ticket"
        :raw="raw"
        :ticket-id="id"
        :sorted-history="sortedHistory"
        :can-edit-ticket="canEditTicket"
        :can-edit-analysis="canEditAnalysis"
        :can-update="canUpdate"
        @reload="loadTicket"
        />

        <!-- Columna 2: Chat / Comentarios -->
        <ticket-chat-column
        :key="'chat-' + id"
        :ticket="ticket"
        :ticket-id="id"
        :comments="comments"
        :can-comment="canComment"
        :current-status-code="currentStatusCode"
        @reload="loadTicket"
        />

        <!-- Columna 3: SLA / Cambio de estado / Resolver -->
        <ticket-sla-column
        :key="'sla-' + id"
        :ticket="ticket"
        :raw="raw"
        :sla="slaData"
        :ticket-id="id"
        :status-actions="statusActions"
        :provider-options="providerOptions"
        @reload="loadTicket"
        />
    </div>
    </b-card>
</div>
</template>

<script>
import {
BCard,
BSpinner,
BButton,
BBadge,
} from "bootstrap-vue";
import { ref, computed, onMounted, watch } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import useTicketCatalogs from "./useTicketCatalogs";
import TicketInfoColumn from "./TicketInfoColumn.vue";
import TicketChatColumn from "./TicketChatColumn.vue";
import TicketSlaColumn from "./TicketSlaColumn.vue";

const PRIORITY_OPTIONS = [
{ value: 1, label: "Muy baja" },
{ value: 2, label: "Baja" },
{ value: 3, label: "Media" },
{ value: 4, label: "Alta" },
{ value: 5, label: "Muy alta" },
{ value: 6, label: "Crítica" },
];
const STATUS_OPTIONS = [
{ value: 1, label: "Nuevo" },
{ value: 2, label: "Asignado" },
{ value: 3, label: "En atención" },
{ value: 4, label: "En espera" },
{ value: 5, label: "Resuelto" },
{ value: 6, label: "Cerrado" },
];

const STATUS_CODE_LABELS = {
NEW: "Nuevo",
ASSIGNED: "Asignado",
PLANNED: "En atención",
PENDING: "En espera",
SOLVED: "Resuelto",
IN_VALIDATION: "En validación",
CLOSED: "Cerrado",
};

const STATUS_ACTIONS = [
{ value: "assign", label: "Asignar", permission: "assign", from: ["NEW"], requiresResponsible: true },
{ value: "reassign", label: "Reasignar proveedor", permission: "special_reasing", from: ["ASSIGNED", "PLANNED", "PENDING", "SOLVED", "IN_VALIDATION"], requiresResponsible: true },
{ value: "plan", label: "Aceptar (En atención)", permission: "attend", from: ["ASSIGNED"] },
{ value: "plan", label: "Reanudar atención", permission: "attend", from: ["PENDING"] },
{ value: "pending", label: "Poner en espera", permission: "attend", from: ["PLANNED"] },
{ value: "solve", label: "Resolver", permission: "solve", from: ["PLANNED", "PENDING"] },
{ value: "validate", label: "Iniciar validación", permission: "validate", from: ["SOLVED"], requiresEvidence: true },
{ value: "close", label: "Cerrar", permission: "close", from: ["IN_VALIDATION"] },
{ value: "reject-validation", label: "Rechazar solución", permission: "validate", from: ["IN_VALIDATION"], requiresComment: true, commentField: "rejection_reason", requiresEvidence: true },
{ value: "reopen", label: "Reabrir", permission: "reopen", from: ["CLOSED"], requiresComment: true, commentField: "reopen_reason" },
];

export default {
name: "TicketDetails",
components: {
    BCard,
    BSpinner,
    BButton,
    BBadge,
    TicketInfoColumn,
    TicketChatColumn,
    TicketSlaColumn,
},
props: {
    id: { type: [Number, String], required: true },
    embedded: { type: Boolean, default: false },
},
setup(props, context) {
    const toast = useToast();

    const loading = ref(false);
    const ticket = ref(null);
    const raw = ref(null);
    const slaData = ref(null);

    const {
    providerOptions,
    loadPriorities,
    loadProviders,
    } = useTicketCatalogs();

    const priorityLabel = (idv) => {
    const f = PRIORITY_OPTIONS.find((o) => o.value === idv);
    return f ? f.label : idv;
    };
    const statusLabel = (idv) => {
    const f = STATUS_OPTIONS.find((o) => o.value === idv);
    return f ? f.label : idv;
    };
    const statusDisplay = (tk) => {
    if (tk.status && tk.status.code === "SOLVED" && tk.sla_clock_running) return "En validación";
    return (tk.status && tk.status.name) || statusLabel(tk.id_status);
    };

    const can = (action) => context.root.$can(action, "HelpdeskTicket");

    const currentStatusCode = computed(() => {
    const tk = raw.value && raw.value.ticket;
    const code = tk && tk.status ? tk.status.code : null;
    if (code === "SOLVED" && tk && tk.sla_clock_running) return "IN_VALIDATION";
    return code;
    });

    const canUpdate = computed(() => can("update"));
    const canEditTicket = computed(
    () => can("update") && !["SOLVED", "IN_VALIDATION", "CLOSED"].includes(currentStatusCode.value)
    );
    // Un ticket cerrado no admite comentarios ni adjuntos (se reabre primero)
    const canComment = computed(() => can("create_message") && currentStatusCode.value !== "CLOSED");
    const canEditAnalysis = computed(
    () => can("attend") && ["PLANNED", "PENDING"].includes(currentStatusCode.value)
    );

    const statusActions = computed(() =>
    STATUS_ACTIONS.filter(
        (a) => a.from.includes(currentStatusCode.value) && can(a.permission)
    )
    );

    const isCommentEvent = (e) => !!(e.glpi_payload && e.glpi_payload.type === "comment");
    const eventUserName = (e) =>
    e.user ? [e.user.name, e.user.first_name].filter(Boolean).join(" ") || e.user.email : "—";

    const comments = computed(() => {
    const tk = (raw.value && raw.value.ticket) || null;
    const events = (tk && tk.events) || [];

    const list = events
        .filter(isCommentEvent)
        .map((e) => ({
        id: e.id,
        user: eventUserName(e),
        title: "Comentario",
        body: e.comment || "",
        evidences: e.evidences || [],
        created_at: e.created_at,
        }))
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    // Primer "comentario": la creación del ticket (mensaje + adjuntos de creación).
    if (tk) {
        const creationEvidences =
        tk.creation_evidences ||
        (events.find((e) => !isCommentEvent(e)) || {}).evidences ||
        [];
        list.unshift({
        id: `creation-${tk.id}`,
        user: eventUserName({ user: tk.user }),
        title: "Creación del ticket",
        body: tk.message || tk.description || "",
        evidences: creationEvidences,
        created_at: tk.created_at,
        isCreation: true,
        });
    }

    return list;
    });

    // Eventos guardados antes de IN_VALIDATION: el inicio de validación quedó como SOLVED → SOLVED
    const historyStatusCode = (e) =>
    e.previous_status === "SOLVED" && e.new_status === "SOLVED" && /^Validación iniciada/.test(e.comment || "")
        ? "IN_VALIDATION"
        : e.new_status;

    const sortedHistory = computed(() => {
    const events = (raw.value && raw.value.ticket && raw.value.ticket.events) || [];
    const list = events
        .filter((e) => !isCommentEvent(e))
        .map((e) => ({
        id: e.id,
        name: STATUS_CODE_LABELS[historyStatusCode(e)] || e.new_status || "—",
        user: { name: eventUserName(e), email: (e.user && e.user.email) || "" },
        created_at: e.created_at,
        comment: e.comment || "",
        }));
    return list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });

    const loadTicket = () => {
    const requestedId = props.id;
    loading.value = true;
    store
        .dispatch("helpdesk/getTicket", { id: requestedId, params: {} })
        .then((response) => {
        // Si ya se navegó a otro ticket, se ignora la respuesta del anterior
        if (String(requestedId) !== String(props.id)) return;
        const data = response && response.data ? response.data : null;
        const tk = data && data.ticket ? data.ticket : null;
        raw.value = data;
        slaData.value = data && data.sla ? data.sla : null;

        if (tk) {
            ticket.value = {
            ...tk,
            estado: statusDisplay(tk),
            _ticket: {
                priority: tk.id_priority,
                priorityLabel:
                (tk.priority && tk.priority.name) || priorityLabel(tk.id_priority),
                message: tk.message || "",
            },
            };
        } else {
            ticket.value = null;
        }
        loading.value = false;
        })
        .catch((error) => {
        if (String(requestedId) !== String(props.id)) return;
        console.error("getTicket ERROR >>>", error);
        loading.value = false;
        toast({
            component: ToastificationContent,
            props: {
            title: error?.response?.data?.message || "No se pudo cargar el ticket.",
            icon: "AlertTriangleIcon",
            variant: "danger",
            },
        });
        });
    };
    loadTicket();

    // La ruta reutiliza este componente al pasar de un ticket a otro (p. ej. desde el menú Helpdesk)
    watch(
    () => props.id,
    (newId, oldId) => {
        if (String(newId) === String(oldId)) return;
        ticket.value = null;
        raw.value = null;
        slaData.value = null;
        loadTicket();
    }
    );

    const goBack = () => {
    context.root.$router.push({ name: "helpdesk_tickets" });
    };

    const badgeFor = (name) => {
    const map = {
        Nuevo: "primary",
        Asignado: "info",
        "En atención": "info",
        "En espera": "warning",
        Resuelto: "success",
        "En validación": "warning",
        Cerrado: "secondary",
        NEW: "primary",
    };
    return map[name] || "secondary";
    };

    onMounted(() => {
    loadPriorities().catch(() => {});
    if (can("assign") || can("special_reasing")) loadProviders().catch(() => {});
    });

    return {
    loading,
    ticket,
    raw,
    slaData,
    comments,
    sortedHistory,
    currentStatusCode,
    canUpdate,
    canEditTicket,
    canEditAnalysis,
    canComment,
    statusActions,
    providerOptions,
    loadTicket,
    goBack,
    badgeFor,
    };
},
};
</script>

<style scoped>
.tk-grid {
display: grid;
grid-template-columns: 0.75fr 1.5fr 0.75fr;
gap: 12px;
align-items: start;
}
@media (max-width: 1200px) {
.tk-grid {
    grid-template-columns: 1fr 1fr;
}
}
@media (max-width: 768px) {
.tk-grid {
    grid-template-columns: 1fr;
}
}
.tk-grid--embedded {
grid-template-columns: 1fr;
}
</style>